/**
 * NewsletterForm.tsx — Five-state brutalist newsletter form
 *
 * States: idle | loading | success | validationError | serverError
 *
 * Design decisions carried through from research:
 *   - D-05: Success renders ONLY after subscribe() returns { kind: 'success' } — never optimistic.
 *   - D-06: Client-side email regex gates submit before any fetch; invalid email returns without
 *           calling subscribe().
 *   - D-08: Submit button disabled synchronously on first submit via a submittingRef guard (Pitfall 4).
 *           The ref is set to true as the very first synchronous act of handleSubmit — before any await —
 *           so two rapid clicks fire subscribe() at most once regardless of React batch timing.
 *   - D-09: Server-error state includes the "Email hello@oakcliff.tech" fallback copy.
 *   - BACK-02 / T-03-SP: Hidden _gotcha honeypot input is UNCONTROLLED (read via ref on submit).
 *           The component reads the field value and forwards it to subscribe() so FormCarry can
 *           drop bot submissions that fill the hidden field server-side.
 *   - NEWS-01: Input and button are disabled only during loading; re-enabled after success so a
 *           repeat signup works without a page reload.
 *   - NEWS-03: aria-live="polite" status region announces every state change.
 *
 * Default export, no required props — mount with <NewsletterForm />.
 */

import React, { useRef, useState } from 'react';
import { subscribe, SubscribeResult } from '../subscribe';
import styles from './NewsletterForm.module.css';

// Client-side email format regex — gates submit before any fetch (D-06)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormStatus = 'idle' | 'loading' | 'success' | 'validationError' | 'serverError';

export default function NewsletterForm(): React.ReactElement {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  /**
   * submittingRef — synchronous guard against double-submit (D-08, Pitfall 4).
   * React state updates are batched; relying on status === 'loading' to disable the
   * button would leave a window of one render where a second click could fire
   * subscribe() concurrently. The ref is set to true immediately (synchronously)
   * at the start of handleSubmit so the guard triggers even before React re-renders.
   */
  const submittingRef = useRef(false);

  /**
   * gotchaRef — uncontrolled ref for the hidden honeypot field (BACK-02, T-03-SP).
   * Bots that fill the hidden field will have a non-empty value; we forward that
   * value to subscribe() so FormCarry can silently drop the submission server-side.
   */
  const gotchaRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // ── Synchronous double-submit guard (D-08, Pitfall 4) ──
    if (submittingRef.current) return;
    submittingRef.current = true;

    // ── Client-side validation (D-06) ──
    if (!email || !EMAIL_REGEX.test(email)) {
      setStatus('validationError');
      setStatusMessage('✕ Please enter a valid email address.');
      submittingRef.current = false;
      return;
    }

    // ── Loading state (D-08 — visible on Slow 3G) ──
    setStatus('loading');
    setStatusMessage('Sending…');

    // Read honeypot value from uncontrolled input (BACK-02) — empty = human
    const gotcha = gotchaRef.current?.value ?? '';

    try {
      const result: SubscribeResult = await subscribe(email, gotcha);

      if (result.kind === 'success') {
        // D-05: success set only after confirmed result
        setStatus('success');
        setStatusMessage('✓ ON THE LIST.');
      } else if (result.kind === 'validation') {
        setStatus('validationError');
        setStatusMessage(`✕ ${result.message || 'Check your email address.'}`);
      } else {
        // server result — always show the fallback contact (D-09)
        setStatus('serverError');
        setStatusMessage('✕ Something went wrong. Email hello@oakcliff.tech');
      }
    } catch {
      // Defensive catch — subscribe() already handles this, but guard anyway
      setStatus('serverError');
      setStatusMessage('✕ Something went wrong. Email hello@oakcliff.tech');
    } finally {
      submittingRef.current = false;
    }
  };

  const isLoading = status === 'loading';
  const isError = status === 'validationError' || status === 'serverError';
  const isSuccess = status === 'success';

  // Status region class varies by state
  const statusClass =
    isSuccess
      ? `${styles.status} ${styles.statusSuccess}`
      : isError
      ? `${styles.status} ${styles.statusError}`
      : styles.status;

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {/* Accessible email label */}
      <label htmlFor="newsletter-email" className={styles.label}>
        Your Email
      </label>

      {/* Input + submit button row */}
      <div className={styles.inputRow}>
        <input
          id="newsletter-email"
          type="email"
          name="email"
          className={isError ? `${styles.input} ${styles.inputError}` : styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={isLoading}
          aria-describedby="newsletter-status"
        />

        <button
          type="submit"
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? 'SENDING…' : 'GET INVOLVED'}
        </button>
      </div>

      {/* Honeypot — hidden from humans and accessibility tree (BACK-02, T-03-SP, Pattern 3).
          Uncontrolled: bots fill it automatically; we read the value via ref on submit
          and forward it to subscribe() so FormCarry drops the request server-side. */}
      <input
        type="text"
        name="_gotcha"
        ref={gotchaRef}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: 'none' }}
      />

      {/* aria-live status region — announces every state change (NEWS-03) */}
      <div
        id="newsletter-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={statusClass}
      >
        {statusMessage}
      </div>

      {/* Subtext hint (idle only) */}
      {status === 'idle' && (
        <p className={styles.hint}>
          No spam. Just meetups, connections, and what's next in Oak Cliff.
        </p>
      )}
    </form>
  );
}
